import React, { Component } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';

import { connect } from 'react-redux';
import BackupForm from './BackupForm';
import BackupTable from './BackupTable';
import XMRButton from '../../components/XMRButton';
import XMSearchBox from '../../components/XMSearchBox';
import * as backupActions from '../../actions/backupActions';
import XMTooltipButton from '../../components/XMTooltipButton';
import withRouter from '../../withRouter';
import _ from 'lodash';
import * as storeActions from '../../actions/storeActions';

@connect((state) => {
  return {
    backup: state.backup,
    store: state.store,
    erpType: state.erpType,
    operator: state.operator,
    dataCentre: state.dataCentre,
  };
})
class Backups extends Component {
  constructor(props) {
    super(props);

    this.state = {
      backupId: null,
      modalClone: false,
      modalCreate: false,
      modalDelete: false,
      modalRestore: false,
      databaseOnly: false,
      creating: false,
      pagination: {},
      storeId: props.router.params.storeId,
    };
  }

  /**
   * XMPagination.doPaginate() will call this and arguments is not passed as object
   *
   * @param page
   * @param size
   * @param sort
   * @param sortDir
   * @param searchRequest
   */
  loadBackups = (
    page = undefined,
    size = undefined,
    sort = undefined,
    sortDir = undefined,
    searchRequest = undefined
  ) => {
    this.clearTimer();
    const { dispatch, router, store } = this.props;
    let storeId = router.params.storeId || store.one.id || this.state.storeId;
    if (storeId) {
      page = page == undefined ? this.state.pagination.page : page;
      size = size || this.state.pagination.size;

      this.setState((state) => ({
        ...state,
        storeId,
        pagination: {
          ...state.pagination,
          page,
          size,
        },
      }));

      dispatch(backupActions.getAllByStore(storeId, page, size, sort, sortDir, searchRequest));
    }
  };

  componentDidMount() {
    this.loadBackups();
    // A direct load or a refresh of /stores/:id/backups never passes through the stores list, so nothing else
    // fetches this store: componentDidUpdate's getOne is gated on the storeId changing, and the constructor
    // already seeded it from the same route param. Without this the header has no store name and the
    // borrowed-CDN warning stays hidden, which is the one sign that uploads into this store will fail.
    const storeId = this.getStoreId();
    if (storeId && !this.getCurrentStore()) {
      this.props.dispatch(storeActions.getOne(storeId));
    }
  }

  isWaiting(backup) {
    return this.isWaitingStatus(backup.statusType) || this.isWaitingStatus(backup.cdnStatusType);
  }

  isWaitingStatus(statusType) {
    return (
      statusType == 'PENDING' || statusType == 'IN_PROGRESS' || statusType == 'TRANSFORMING' || statusType == 'QUEUED'
    );
  }

  getStoreId() {
    const { router, store } = this.props;
    return router.params.storeId || store.one.id || this.state.storeId;
  }

  toggleCreate = () => {
    // The backdrop, Esc and the header cross all reach this, not only Cancel, so the `creating` guard has to
    // live here rather than on the buttons. Closing mid-request clears the error and leaves the failure with
    // nowhere to render, which is the silent failed create the dialog was made to stay open for.
    if (this.state.creating) {
      return;
    }
    // Clear any error left by an earlier attempt, so the dialog never opens showing a stale one.
    this.props.dispatch(backupActions.clearError());
    this.setState({ modalCreate: !this.state.modalCreate, databaseOnly: false });
  };

  // The dialog stays open until the backup is queued, so a rejected create does not look like a started one.
  // That leaves the button live for the length of the request, so `creating` stops a second click queueing
  // a second backup.
  handleCreate = () => {
    const { dispatch } = this.props;
    if (this.state.creating) {
      return;
    }
    this.setState({ creating: true });
    dispatch(backupActions.create(this.getStoreId(), this.state.databaseOnly)).then(created => {
      this.setState({ creating: false });
      if (!created) {
        return;
      }
      this.setState({ modalCreate: false, databaseOnly: false });
      this.loadBackups();
    });
  };

  toggleClone = (backupId) => {
    const { dispatch } = this.props;
    this.setState({
      backupId: backupId,
      modalClone: !this.state.modalClone,
    });
    dispatch(backupActions.clear());
    if (this.state.modalClone) {
      this.loadBackups();
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const {
      backup: { all },
      router,
      dispatch,
    } = this.props;

    let storeId = this.getStoreId();

    if (storeId != prevState.storeId) {
      dispatch(storeActions.getOne(storeId));
      this.loadBackups();

      // Update the state storeId
      this.setState({
        storeId: storeId,
      });
      return;
    }

    if (!router.params.storeId && storeId) {
      // load the URL with the storeId
      this.redirectTo(storeId);
      return;
    }

    if (all !== prevProps.store.all) {
      // any backup with waiting status?
      let backupWaiting = _.find(all, (b) => {
        return this.isWaiting(b);
      });

      // TODO: move the "update time" (10s) to a Settings property?
      if (backupWaiting && !this.timerId) {
        this.timerId = _.delay(() => {
          if (!this.state.modalClone) {
            this.loadBackups();
          }
        }, 10000);
      }
    }
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  clearTimer() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  redirectTo = (storeId) => {
    this.props.router.navigate(`/stores/${storeId}/backups`);
  };

  // store.all is only filled when you arrive from the stores list, so a direct load or a refresh of
  // /stores/:id/backups finds nothing there. store.one is loaded for the current store either way.
  getCurrentStore = () => {
    const { store } = this.props;
    const storeId = this.getStoreId();
    const fromList = store.all.find((s) => s.id == storeId);
    if (fromList) {
      return fromList;
    }
    return store.one && store.one.id == storeId ? store.one : undefined;
  };

  getStoreName = () => {
    const currentStore = this.getCurrentStore();
    return (currentStore && currentStore.name) || '';
  };

  // Set by a database-only restore. Without it there is no sign that this store's images come from elsewhere.
  getCdnSourceStoreCode = () => {
    const currentStore = this.getCurrentStore();
    return (currentStore && currentStore.cdnSourceStoreCode) || '';
  };

  // Only set when the borrowed store is in another environment. A store code is not unique across
  // environments, so without this an operator reads a local store's name and goes looking for the wrong one.
  getCdnSourceEnvironmentCode = () => {
    const currentStore = this.getCurrentStore();
    return (currentStore && currentStore.cdnSourceEnvironmentCode) || '';
  };

  handleSearch = (fuzzyString) => {
    this.loadBackups(undefined, undefined, undefined, undefined, fuzzyString);
  };

  render() {
    const { backup } = this.props;
    return (
      <div>
        <Card>
          <CardBody>
            <Row className="pb-3">
              <Col md="5">
                <h3>Store: {this.getStoreName()}</h3>
                {this.getCdnSourceStoreCode() ? (
                  <span className="text-warning" title="Restored from a database-only backup. CDN uploads into this store will not work.">
                    CDN media borrowed from {this.getCdnSourceStoreCode()}
                    {this.getCdnSourceEnvironmentCode() ? ` in ${this.getCdnSourceEnvironmentCode()}` : ''}
                  </span>
                ) : null}
              </Col>
              <Col md="4">
                <XMSearchBox onSearch={this.handleSearch} />
              </Col>
              <Col md="3">
                <ButtonGroup>
                  <XMRButton loading={backup.loading} handleRefresh={this.loadBackups} />
                  <XMTooltipButton
                    icon="icon-cloud-download"
                    color="light"
                    tooltipTitle="Create"
                    onClick={this.toggleCreate}
                  />
                  <XMTooltipButton icon="fa-solid fa-clone" color="light" tooltipTitle="Clone" onClick={this.toggleClone} />
                </ButtonGroup>
              </Col>
            </Row>

            <Modal isOpen={this.state.modalCreate} toggle={this.toggleCreate}>
              <ModalHeader toggle={this.toggleCreate}>Create Backup</ModalHeader>
              <ModalBody>
                {/* The list's own alert only renders once a store has backups, so a failed first create
                    would otherwise report nothing at all. */}
                <Alert color="danger" isOpen={!!backup.error}>
                  {backup.error}
                </Alert>
                <FormGroup check>
                  <Input
                    type="checkbox"
                    id="databaseOnly"
                    checked={this.state.databaseOnly}
                    onChange={e => this.setState({ databaseOnly: e.target.checked })}
                  />
                  <Label check htmlFor="databaseOnly">
                    Database only — skip the CDN media backup
                  </Label>
                </FormGroup>
                <p className="text-muted mt-3 mb-0">
                  A CDN backup is slow for large stores and needs storage space to keep. A store restored from a
                  database-only backup reads its images from this store, and CDN uploads into it will not work.
                  Choose a full backup when the store is moving to another environment.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button
                  type="button"
                  color="secondary"
                  className="me-2"
                  disabled={this.state.creating}
                  onClick={this.toggleCreate}
                >
                  Cancel
                </Button>
                <Button type="button" color="primary" disabled={this.state.creating} onClick={this.handleCreate}>
                  {this.state.creating ? 'Creating…' : 'Create'}
                </Button>
              </ModalFooter>
            </Modal>

            <Modal isOpen={this.state.modalClone} toggle={this.toggleClone} className="lg" style={{ minWidth: '60%' }}>
              <ModalHeader toggle={this.toggleClone}>
                Clone Store{' '}
                <span style={{ color: 'grey', fontSize: '0.6em' }}>
                  Please select the store you which to clone into this store
                </span>
              </ModalHeader>
              <ModalBody>
                <BackupForm storeId={this.getStoreId()} handleCancel={this.toggleClone} toggle={this.toggleClone} />
              </ModalBody>
            </Modal>

            <BackupTable
              storeId={this.getStoreId()}
              all={backup.all}
              pagination={backup.pagination}
              paginate={this.loadBackups}
            />
          </CardBody>
        </Card>
      </div>
    );
  }
}

// Named so a test can reach the class through connect's WrappedComponent. The default export is wrapped
// twice, and withRouter keeps no handle on what it wraps.
export { Backups };

export default withRouter(Backups);
